import { Controller } from '@hotwired/stimulus';
import { type ModalController } from './modal';

export class AdminDashboardController extends Controller {
    static outlets = ['modal'];
    declare readonly modalOutlets: ModalController[];

    static targets = [
        'bannerMessageInput',
        'bannerPreviewContainer',
        'bannerPreviewMessage',
        'hiddenBannerInput',
        'userSearchInput',
        'userTableBody'
    ];

    declare readonly bannerMessageInputTarget: HTMLInputElement;
    declare readonly bannerPreviewContainerTarget: HTMLElement;
    declare readonly bannerPreviewMessageTarget: HTMLElement;
    declare readonly hiddenBannerInputTarget: HTMLInputElement;
    declare readonly userSearchInputTarget: HTMLInputElement;
    declare readonly userTableBodyTarget: HTMLElement;
    private pendingDeleteForm: HTMLFormElement | null = null;
    private pendingDeleteCodeForm: HTMLFormElement | null = null;
    private pendingMakeAdminForm: HTMLFormElement | null = null;
    private pendingRevokeAdminForm: HTMLFormElement | null = null;
    private readonly confirmedForms = new WeakSet<HTMLFormElement>();
    private readonly handleSoftDeleteConfirmedBound = this.handleSoftDeleteConfirmed.bind(this);
    private readonly handleHardDeleteChoiceBound = this.handleHardDeleteChoice.bind(this);
    private readonly handleHardDeleteConfirmedBound = this.handleHardDeleteConfirmed.bind(this);
    private readonly handleDeleteCodeConfirmedBound = this.handleDeleteCodeConfirmed.bind(this);
    private readonly handleMakeAdminConfirmedBound = this.handleMakeAdminConfirmed.bind(this);
    private readonly handleRevokeAdminConfirmedBound = this.handleRevokeAdminConfirmed.bind(this);

    connect(): void {
        document.addEventListener('ova:admin:confirm-soft-delete', this.handleSoftDeleteConfirmedBound);
        document.addEventListener('ova:admin:choose-hard-delete', this.handleHardDeleteChoiceBound);
        document.addEventListener('ova:admin:confirm-hard-delete', this.handleHardDeleteConfirmedBound);
        document.addEventListener('ova:admin:confirm-delete-code', this.handleDeleteCodeConfirmedBound);
        document.addEventListener('ova:admin:confirm-make-admin', this.handleMakeAdminConfirmedBound);
        document.addEventListener('ova:admin:confirm-revoke-admin', this.handleRevokeAdminConfirmedBound);
    }

    disconnect(): void {
        document.removeEventListener('ova:admin:confirm-soft-delete', this.handleSoftDeleteConfirmedBound);
        document.removeEventListener('ova:admin:choose-hard-delete', this.handleHardDeleteChoiceBound);
        document.removeEventListener('ova:admin:confirm-hard-delete', this.handleHardDeleteConfirmedBound);
        document.removeEventListener('ova:admin:confirm-delete-code', this.handleDeleteCodeConfirmedBound);
        document.removeEventListener('ova:admin:confirm-make-admin', this.handleMakeAdminConfirmedBound);
        document.removeEventListener('ova:admin:confirm-revoke-admin', this.handleRevokeAdminConfirmedBound);
    }

    private getModalById(id: string): ModalController {
        const modal = this.modalOutlets.find((outlet) => outlet.element.id === id);
        if (!modal) {
            throw new Error(`Modal with id "${id}" not found`);
        }
        return modal;
    }

    openDeleteModeModal(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement | null;
        const form = button?.closest('form') as HTMLFormElement | null;
        if (!form) {
            return;
        }

        this.pendingDeleteForm = form;
        this.getModalById('admin-delete-mode-modal').show();
    }

    openMakeAdminModal(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement | null;
        const form = button?.closest('form') as HTMLFormElement | null;
        if (!form) {
            return;
        }

        this.pendingMakeAdminForm = form;
        this.getModalById('admin-make-admin-modal').show();
    }

    openRevokeAdminModal(event: Event): void {
        event.preventDefault();
        const button = event.currentTarget as HTMLElement | null;
        const form = button?.closest('form') as HTMLFormElement | null;
        if (!form) {
            return;
        }

        this.pendingRevokeAdminForm = form;
        this.getModalById('admin-revoke-admin-modal').show();
    }

    private handleSoftDeleteConfirmed(): void {
        this.submitPendingDeleteForm('soft');
    }

    private handleHardDeleteChoice(): void {
        this.getModalById('admin-hard-delete-warning-modal').show();
    }

    private handleHardDeleteConfirmed(): void {
        this.submitPendingDeleteForm('hard');
    }

    private handleDeleteCodeConfirmed(): void {
        if (!this.pendingDeleteCodeForm) {
            return;
        }
        this.confirmedForms.add(this.pendingDeleteCodeForm);
        this.pendingDeleteCodeForm.requestSubmit();
        this.pendingDeleteCodeForm = null;
    }

    private handleMakeAdminConfirmed(): void {
        if (!this.pendingMakeAdminForm) {
            return;
        }
        this.confirmedForms.add(this.pendingMakeAdminForm);
        this.pendingMakeAdminForm.requestSubmit();
        this.pendingMakeAdminForm = null;
    }

    private handleRevokeAdminConfirmed(): void {
        if (!this.pendingRevokeAdminForm) {
            return;
        }
        this.confirmedForms.add(this.pendingRevokeAdminForm);
        this.pendingRevokeAdminForm.requestSubmit();
        this.pendingRevokeAdminForm = null;
    }

    private submitPendingDeleteForm(deleteMode: 'soft' | 'hard'): void {
        if (!this.pendingDeleteForm) {
            return;
        }

        const deleteModeInput = this.pendingDeleteForm.querySelector('input[name="delete_mode"]') as HTMLInputElement | null;
        if (deleteModeInput) {
            deleteModeInput.value = deleteMode;
        }
        this.pendingDeleteForm.requestSubmit();
        this.pendingDeleteForm = null;
    }

    // Banner Preview Functions
    previewBanner(): void {
        const message = this.bannerMessageInputTarget.value.trim();
        if (message !== '') {
            this.bannerPreviewMessageTarget.textContent = message;
            this.hiddenBannerInputTarget.value = message;
            this.bannerPreviewContainerTarget.classList.remove('hidden');
        }
    }

    cancelPreview(): void {
        this.bannerPreviewContainerTarget.classList.add('hidden');
    }

    // User Search Filter
    filterUsers(): void {
        const filter = this.userSearchInputTarget.value.toLowerCase();
        const rows = this.userTableBodyTarget.querySelectorAll('tr');

        rows.forEach((row: Element) => {
            const cells = row.querySelectorAll('td');
            if (cells.length < 2) {return;}

            const idCell = cells[0].textContent?.toLowerCase() || '';
            const emailCell = cells[1].textContent?.toLowerCase() || '';
            const match = idCell.includes(filter) || emailCell.includes(filter);

            (row as HTMLElement).style.display = match ? '' : 'none';
        });
    }

    confirmDeleteCode(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.confirmedForms.has(form)) {
            this.confirmedForms.delete(form);
            return;
        }
        event.preventDefault();
        this.pendingDeleteCodeForm = form;
        this.getModalById('admin-delete-referral-code-modal').show();
    }

    confirmMakeAdmin(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.confirmedForms.has(form)) {
            this.confirmedForms.delete(form);
            return;
        }
        event.preventDefault();
    }

    confirmRevokeAdmin(event: Event): void {
        const form = event.currentTarget as HTMLFormElement;
        if (this.confirmedForms.has(form)) {
            this.confirmedForms.delete(form);
            return;
        }
        event.preventDefault();
    }
}
